import { DepartmentType } from 'app/data/Enums';
import { MouseController } from 'app/chrome/mouse/MouseController';
import { HomeViewConfig } from 'app/model/HomeViewConfig';
import { HomeTimeline } from 'app/views/home/HomeTimeline';
import type { HomeTimelineDeps } from 'app/views/home/timeline/timeline.types';
import type { HomeWebGLDeps, HomeWebGLPreloadReports } from 'app/views/home/webgl/webgl.types';
import { HomeSceneDeps } from './home.types';

/**
 * Pixi side of the home section: the home container, the particles
 * spritesheet and the HomeTimeline (+ moth) wiring.
 */
export class HomeScene {
	private _deps: HomeSceneDeps;

	private _mainTimeline: HomeTimeline;

	private _$homeContainer: PIXI.Container;
	private _pixiSpritesheet: PIXI.Spritesheet;

	constructor(deps: HomeSceneDeps) {
		this._deps = deps;
	}

	//Accessors
	//-------------------------------------------------------------------------------------

	public get container(): PIXI.Container {
		return this._$homeContainer;
	}

	public get timeline(): HomeTimeline {
		return this._mainTimeline;
	}

	//Pixi dependecy (set in AppController via HomeController):
	public setPixi($pixi: PIXI.Application, $homeContainer: PIXI.Container): void {
		this._$homeContainer = $homeContainer;

		if (this._$homeContainer && !this._deps.isActive()) {
			this._$homeContainer.visible = false;
			this._$homeContainer.y = -3000;
			this._$homeContainer.alpha = 0;
		}
	}

	public setParticlesSpritesheet(spritesheet: PIXI.Spritesheet): void {
		this._pixiSpritesheet = spritesheet;
	}

	//Creation
	//-------------------------------------------------------------------------------------

	//Create the different animations for x and y movement on home:
	public createTimelines(
		mouseController: MouseController,
		json: HomeViewConfig,
		container: Element,
		workDeeplinkDepartment: DepartmentType,
		virtualWidth: number,
		viewPortCount: number,
		timelineDeps: HomeTimelineDeps,
		preloadReports: HomeWebGLPreloadReports
	): void {
		let progress = 0;

		if (workDeeplinkDepartment) {
			progress = workDeeplinkDepartment === DepartmentType.SCREENS ? 0 : 1;
		}

		//The home WebGL layer's host callbacks (see HomeWebGLDeps). The preload
		//reports come from HomeController (which owns the PreloadController
		//reference); the rest resolve against the timeline — lazily, via
		//closures, since _mainTimeline is only assigned on the next line
		//(reportPixelDataReady is the one dep that fires during the
		//constructor, and it touches only the preload side):
		const webglDeps: HomeWebGLDeps = {
			reportPixelDataReady: () => preloadReports.reportPixelDataReady(),
			reportSceneCreated: () => preloadReports.reportSceneCreated(),
			onReady: () => this._mainTimeline.onWebGLReady(),
			getVirtualWidth: () => this._mainTimeline.virtualWidth,
			getSceneProps: () => this._mainTimeline.sceneProps,
			colouriseSpacesItems: (colourSet, screenColour, floorColour) =>
				this._mainTimeline.colouriseSpacesScene(colourSet, screenColour, floorColour),
			colouriseSystemsItems: (colourSet, instant) => this._mainTimeline.colouriseSystemsScene(colourSet, instant)
		};

		this._mainTimeline = new HomeTimeline(mouseController, json, timelineDeps, webglDeps);
		this._mainTimeline.setVirtualWidth(virtualWidth, viewPortCount);

		//Pass the main:
		this._mainTimeline.setUIRefs(
			container.querySelector('.home__intro'),
			container.querySelector('.home__screens'),
			container.querySelector('.home__spaces'),
			container.querySelector('.ui__paging__home'),
			container.querySelector('.home__systems'),
			container as HTMLElement
		);

		this._mainTimeline.create();

		this._mainTimeline.setProgress(progress);
		this._mainTimeline.createScene(this._$homeContainer, this._pixiSpritesheet);
		this._mainTimeline.setVirtualWidth(virtualWidth, viewPortCount);
		this._mainTimeline.onCompleteExit = () => this._deps.onCompleteExit();
	}


	//State
	//-------------------------------------------------------------------------------------

	/** Set the timeline interaction to on/off */
	public setTimelineActive(enabled: boolean): void {
		if (this._mainTimeline) {
			this._mainTimeline.setActive(enabled);
		} else {
			console.warn('_mainTimeline not set');
		}
	}

	//Resize
	//-------------------------------------------------------------------------------------

	/**
	 * Re-pivot/position the home container. (The particle-grid-target resize
	 * that used to live here is swap-side — it moved into the WebGL layer's
	 * resize, fed by the timeline's resize pass.)
	 */
	public applyContainerResize(innerWidth: number, innerHeight: number, active: boolean): void {
		this._$homeContainer.pivot.y = innerHeight / 2;

		if (active) {
			this._$homeContainer.y = innerHeight / 2;
		}
	}
}
